import { create, StoreApi } from 'zustand';
import { Email, Mailbox } from '@/types/jmap.types';

interface EmailListCacheEntry {
  emails: Email[];
  fetchedAt: number;
}

interface EmailDetailCacheEntry {
  email: Email;
  fetchedAt: number;
}

interface UserAvatarCacheEntry {
  url: string | null;
  fetchedAt: number;
}

interface EmailState {
  mailboxes: Mailbox[];
  emails: Email[];
  emailListCache: Record<string, EmailListCacheEntry>;
  emailDetailCache: Record<string, EmailDetailCacheEntry>;
  favoriteMailboxIds: string[];
  selectedMailbox: string | null;
  selectedEmail: string | null;
  drawerOpen: boolean;
  newMailCount: number;
  newCalendarCount: number;
  mailListRefreshToken: number;
  accountEmail: string | null;
  accountDisplayName: string | null;
  accountAvatarUrl: string | null;
  userAvatarCache: Record<string, UserAvatarCacheEntry>;
  setMailboxes: (mailboxes: Mailbox[]) => void;
  setEmails: (emails: Email[]) => void;
  setEmailListCache: (key: string, emails: Email[], fetchedAt?: number) => void;
  setEmailDetailCache: (email: Email, fetchedAt?: number) => void;
  clearEmailListCache: (key?: string) => void;
  clearEmailDetailCache: (emailId?: string) => void;
  setFavoriteMailboxIds: (ids: string[]) => void;
  toggleFavoriteMailbox: (mailboxId: string) => void;
  selectMailbox: (mailboxId: string) => void;
  selectEmail: (emailId: string | null) => void;
  removeEmail: (emailId: string) => void;
  updateEmail: (emailId: string, updates: Partial<Email>) => void;
  toggleDrawer: () => void;
  setDrawerOpen: (open: boolean) => void;
  incrementNewMailCount: (count?: number) => void;
  resetNewMailCount: () => void;
  incrementNewCalendarCount: (count?: number) => void;
  resetNewCalendarCount: () => void;
  bumpMailListRefresh: () => void;
  setAccountProfile: (email: string | null, displayName?: string | null, avatarUrl?: string | null) => void;
  setAccountAvatarUrl: (avatarUrl: string | null) => void;
  setUserAvatarCache: (email: string, url: string | null, fetchedAt?: number) => void;
  clearUserAvatarCache: (email?: string) => void;
  resetStore: () => void;
}

const loadFavoriteMailboxIds = (): string[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem('webmail_favorite_mailboxes');
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
};

const persistFavoriteMailboxIds = (ids: string[]): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem('webmail_favorite_mailboxes', JSON.stringify(ids));
  } catch {}
};

const clearFavoriteMailboxIds = (): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem('webmail_favorite_mailboxes');
  } catch {}
};

type SetState = StoreApi<EmailState>['setState'];

export const useEmailStore = create<EmailState>((set: SetState) => ({
  mailboxes: [],
  emails: [],
  emailListCache: {},
  emailDetailCache: {},
  favoriteMailboxIds: loadFavoriteMailboxIds(),
  selectedMailbox: null,
  selectedEmail: null,
  drawerOpen: false,
  newMailCount: 0,
  newCalendarCount: 0,
  mailListRefreshToken: 0,
  accountEmail: null,
  accountDisplayName: null,
  accountAvatarUrl: null,
  userAvatarCache: {},
  setMailboxes: (mailboxes: Mailbox[]) => set({ mailboxes }),
  setEmails: (emails: Email[]) =>
    set({
      emails: [...emails].sort((a: Email, b: Email) => {
        const da = a.receivedAt ? new Date(a.receivedAt).getTime() : 0;
        const db = b.receivedAt ? new Date(b.receivedAt).getTime() : 0;
        return db - da; // newest first
      }),
    }),
  setEmailListCache: (key: string, emails: Email[], fetchedAt: number = Date.now()) =>
    set((state: EmailState) => ({
      emailListCache: {
        ...state.emailListCache,
        [key]: { emails, fetchedAt },
      },
    })),
  setEmailDetailCache: (email: Email, fetchedAt: number = Date.now()) =>
    set((state: EmailState) => ({
      emailDetailCache: {
        ...state.emailDetailCache,
        [email.id]: { email, fetchedAt },
      },
    })),
  clearEmailListCache: (key?: string) =>
    set((state: EmailState) => {
      if (!key) return { emailListCache: {} };
      const next = { ...state.emailListCache };
      delete next[key];
      return { emailListCache: next };
    }),
  clearEmailDetailCache: (emailId?: string) =>
    set((state: EmailState) => {
      if (!emailId) return { emailDetailCache: {} };
      const next = { ...state.emailDetailCache };
      delete next[emailId];
      return { emailDetailCache: next };
    }),
  setFavoriteMailboxIds: (ids: string[]) => {
    persistFavoriteMailboxIds(ids);
    set({ favoriteMailboxIds: ids });
  },
  toggleFavoriteMailbox: (mailboxId: string) =>
    set((state: EmailState) => {
      const exists = state.favoriteMailboxIds.includes(mailboxId);
      const next = exists
        ? state.favoriteMailboxIds.filter((id: string) => id !== mailboxId)
        : [...state.favoriteMailboxIds, mailboxId];
      persistFavoriteMailboxIds(next);
      return { favoriteMailboxIds: next };
    }),
  selectMailbox: (mailboxId: string) =>
    set((state: EmailState) => {
      if (state.selectedMailbox === mailboxId) return state;
      return { selectedMailbox: mailboxId, emails: [] };
    }),
  selectEmail: (emailId: string | null) => set({ selectedEmail: emailId }),
  removeEmail: (emailId: string) =>
    set((state: EmailState) => {
      const nextCacheEntries: [string, EmailListCacheEntry][] = Object.entries(state.emailListCache).map(
        ([key, entry]: [string, EmailListCacheEntry]) => {
          return [key, { ...entry, emails: entry.emails.filter((email: Email) => email.id !== emailId) }];
        }
      );
      return {
        emails: state.emails.filter((email: Email) => email.id !== emailId),
        selectedEmail: state.selectedEmail === emailId ? null : state.selectedEmail,
        emailDetailCache: Object.keys(state.emailDetailCache).length
          ? Object.fromEntries(
              Object.entries(state.emailDetailCache).filter(([key]: [string, EmailDetailCacheEntry]) => key !== emailId)
            )
          : state.emailDetailCache,
        emailListCache: Object.fromEntries(nextCacheEntries),
      };
    }),
  updateEmail: (emailId: string, updates: Partial<Email>) =>
    set((state: EmailState) => {
      const applyUpdates = (email: Email): Email => {
        if (email.id !== emailId) return email;
        // Merge keywords map carefully to avoid losing other flags
        if (updates.keywords) {
          const merged: Record<string, boolean> = { ...(email.keywords || {}) };
          Object.entries(updates.keywords).forEach(([k, v]: [string, boolean | null]) => {
            if (v === null) {
              delete merged[k];
            } else {
              merged[k] = v;
            }
          });
          return { ...email, ...updates, keywords: merged };
        }
        return { ...email, ...updates };
      };
      const nextCacheEntries: [string, EmailListCacheEntry][] = Object.entries(state.emailListCache).map(
        ([key, entry]: [string, EmailListCacheEntry]) => {
          return [key, { ...entry, emails: entry.emails.map(applyUpdates) }];
        }
      );
      const existingDetail = state.emailDetailCache[emailId];
      const nextDetailCache = existingDetail
        ? { ...state.emailDetailCache, [emailId]: { ...existingDetail, email: applyUpdates(existingDetail.email) } }
        : state.emailDetailCache;
      return {
        emails: state.emails.map(applyUpdates),
        emailListCache: Object.fromEntries(nextCacheEntries),
        emailDetailCache: nextDetailCache,
      };
    }),
  toggleDrawer: () => set((state: EmailState) => ({ drawerOpen: !state.drawerOpen })),
  setDrawerOpen: (open: boolean) => set({ drawerOpen: open }),
  incrementNewMailCount: (count: number = 1) =>
    set((state: EmailState) => ({ newMailCount: state.newMailCount + Math.max(count, 0) })),
  resetNewMailCount: () => set({ newMailCount: 0 }),
  incrementNewCalendarCount: (count: number = 1) =>
    set((state: EmailState) => ({ newCalendarCount: state.newCalendarCount + Math.max(count, 0) })),
  resetNewCalendarCount: () => set({ newCalendarCount: 0 }),
  bumpMailListRefresh: () => set((state: EmailState) => ({ mailListRefreshToken: state.mailListRefreshToken + 1 })),
  setAccountProfile: (email: string | null, displayName: string | null = null, avatarUrl: string | null = null) =>
    set((state: EmailState) => ({
      accountEmail: email,
      accountDisplayName: displayName,
      accountAvatarUrl: avatarUrl,
      userAvatarCache: state.accountEmail && state.accountEmail !== email ? {} : state.userAvatarCache,
    })),
  setAccountAvatarUrl: (avatarUrl: string | null) => set({ accountAvatarUrl: avatarUrl }),
  setUserAvatarCache: (email: string, url: string | null, fetchedAt: number = Date.now()) =>
    set((state: EmailState) => ({
      userAvatarCache: {
        ...state.userAvatarCache,
        [email]: { url, fetchedAt },
      },
    })),
  clearUserAvatarCache: (email?: string) =>
    set((state: EmailState) => {
      if (!email) return { userAvatarCache: {} };
      const next = { ...state.userAvatarCache };
      delete next[email];
      return { userAvatarCache: next };
    }),
  resetStore: () =>
    set(() => {
      clearFavoriteMailboxIds();
      return {
        mailboxes: [],
        emails: [],
        emailListCache: {},
        emailDetailCache: {},
        favoriteMailboxIds: [],
        selectedMailbox: null,
        selectedEmail: null,
        drawerOpen: false,
        newMailCount: 0,
        newCalendarCount: 0,
        mailListRefreshToken: 0,
        accountEmail: null,
        accountDisplayName: null,
        accountAvatarUrl: null,
        userAvatarCache: {},
      };
    }),
}));
